/**
 * @file store/routeStore.tsx
 * @description 路由与页面粒度的共享状态：如当前激活路由、面包屑或跳转参数等（按需扩展）。
 */
import i18next from 'i18next'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { create } from 'zustand'

/**
 * 路由信息，用于顶部路由
 */
type Route = {
  path: string
  name: string
}

type State = {
  currentRouteInfos: Route[]
  /** MCP 创建页进行中标志：true 时菜单跳转需二次确认 */
  mcpNavGuard: boolean
}

type Actions = {
  /**
   * 更新顶部路由信息
   * @param routeInfos 路由数组 [{ path, name }]
   */
  updateCurrentRouteInfos: (routeInfos: Route[]) => void
  setMcpNavGuard: (v: boolean) => void
}

export const useRouteStore = create<State & Actions>(set => ({
  currentRouteInfos: [],
  updateCurrentRouteInfos: routeInfo => set(() => ({ currentRouteInfos: routeInfo })),
  mcpNavGuard: false,
  setMcpNavGuard: v => set({ mcpNavGuard: v })
}))

/**
 * 涉及顶部路由信息，在对应页面进行处理，传入路由名称列表，
 * 例如：
 * 知识库 > 详情 > 文件详情 传入对应信息为 [知识库, 详情, 文件详情]
 * 为方便后期扩展中英文和名称都由加载页面时手动传入
 */
/**
 * 便捷 Hook：根据当前 URL 与传入的中文名列表，生成顶部路由数组
 * @param pathNameList 路由名称列表（与 URL 片段一一对应；数量可小于等于片段数）
 */
export function useRouteStoreHook(pathNameList = []) {
  if (pathNameList.length === 0) return

  const location = useLocation()
  const { updateCurrentRouteInfos } = useRouteStore()

  useEffect(() => {
    const paths = location.pathname.split('/').filter(path => path)
    if (pathNameList.length > paths.length && location.pathname !== '/') {
      console.error('pathNameList 长度超过path配置', pathNameList, paths)
      return
    }

    const routeInfos = []
    let realPaths = [...paths]

    // path数组长度大于pathNameList有效长度时，从数组0下标开始做数据合并
    const diffLen = paths.length - pathNameList.length
    if (diffLen !== 0) {
      let startPath = paths[0]
      for (let i = 1; i < diffLen + 1; i++) {
        startPath = startPath + `/${paths[i]}`
      }
      realPaths = [startPath].concat(paths.slice(diffLen + 1))
    }

    realPaths.reduce((path, currentPath, index) => {
      routeInfos.push({
        path: path + `/${currentPath}`,
        name: pathNameList[index]
      })
      return path + `/${currentPath}`
    }, '')

    console.log('updateCurrentRouteInfos:', routeInfos)
    updateCurrentRouteInfos(routeInfos)
  }, [i18next.language])
}
